/**
 * This file defines an Iterator interface. It is a compatible, but simplified, version of the ES6 iterator interface. Even with the
 * downlevelIteration Typescript compiler flag, we can't use iterators on arbitrary objects without a symbol polyfill. Hence, for maximum
 * compatibility, these interfaces are redefined here so we don't have to rely on ES6 at compile time.
 *
 * Full type definitions can be found at:
 * https://github.com/Microsoft/TypeScript/blob/master/lib/lib.es2015.iterable.d.ts
 */

import { assertNever } from './common/TypeScriptUtility';

export type IteratorProvider<S, T> = (s: S) => Iterator<T>;

/**
 * An iterator that can traverse data structures. This is a simplified version of the ES6 iterator interface.
 */
export interface Iterator<T> {
  next(): IteratorResult<T>;
}

/**
 * The ES2015 typescript definitions use a different shape for IteratorResult, but actual built-in browser implementations (tested on Chrome
 * and Edge) return value: undefined when the iteration is done, so this union interface allows that behavior.
 */
export type IteratorResult<T> = IteratorResultNotDone<T> | IteratorResultDone;

export interface IteratorResultNotDone<T> {
  readonly done: false;
  readonly value: T;
}

export interface IteratorResultDone {
  readonly done: true;
}

/**
 * Smart constructor for creating an iterator result that is not done.
 *
 * @param value - The next value.
 */
export function iteratorResultNotDone<T>(value: T): IteratorResult<T> {
  return {
    done: false,
    value
  };
}

/**
 * A value for representing an iterator result that is done.
 */
export const iteratorResultDone: IteratorResultDone = {
  done: true
};

/**
 * Higher-order helper function for iterator results.
 *
 * @param f - Function to apply to the next value.
 * @param t - Default value to return when applied to an iterator result that is done.
 */
export function iteratorResult<S, T>(f: (s: S) => T, t: T): (ir: IteratorResult<S>) => T {
  return (ir: IteratorResult<S>) => {
    switch (ir.done) {
      case false: return f(ir.value);
      case true: return t;
      default: return assertNever(ir);
    }
  };
}

/**
 * Returns the next value or throws an exception if given an iterator result that is done.
 *
 * @param ir - Iterator result.
 * @throws Error if given an iterator result that is done.
 */
export function fromIteratorResultNotDone<T>(ir: IteratorResult<T>): T {
  switch (ir.done) {
    case false: return ir.value;
    default: throw new Error('IteratorResultDone');
  }
}

/**
 * Returns the next value or a given default if applied to an iterator result that is done.
 *
 * @param t - Default value.
 */
export function fromIteratorResult<T>(t: T): (ir: IteratorResult<T>) => T {
  return iteratorResult(v => v, t);
}
